import React from "react";

// HowToUse.jsx — drop this into your src/pages (or src/components) and add a route
// Dark theme + Tailwind-only. No extra deps.
// Optional: pass onNavigate(path) to programmatically navigate from CTA buttons.

export default function HowToUse({ onNavigate }) {
    const go = (path) => () => (onNavigate ? onNavigate(path) : null);

    const Section = ({ id, title, children }) => (
        <section id={id} className="scroll-mt-24">
            <h2 className="text-xl md:text-2xl font-semibold text-white mt-10 mb-3">
                {title}
            </h2>
            <div className="text-sm md:text-base text-zinc-300 leading-relaxed space-y-3">
                {children}
            </div>
        </section>
    );

    const Pill = ({ children }) => (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-zinc-800/70 border border-zinc-700 text-zinc-200 text-xs">
            {children}
        </span>
    );

    const Card = ({ children, className = "" }) => (
        <div className={`rounded-2xl border border-zinc-800/80 bg-zinc-900/60 shadow-xl shadow-black/30 ${className}`}>
            {children}
        </div>
    );

    const Li = ({ children }) => (
        <li className="pl-2 marker:text-zinc-500 text-zinc-300">{children}</li>
    );

    return (
        <div className="min-h-screen w-full bg-black/95">
            <div className="mx-auto max-w-5xl px-4 sm:px-6 md:px-8 py-10">
                {/* Header */}
                <header className="flex flex-col gap-5 md:gap-6">
                    <div className="flex items-center justify-between">
                        <h1 className="text-2xl md:text-4xl font-bold tracking-tight text-white">
                            How to Use <span className="text-emerald-400">MyStockPicksAI</span>
                        </h1>
                        <div className="hidden md:flex items-center gap-2">
                            <Pill>AI Picks</Pill>
                            <Pill>Options</Pill>
                            <Pill>Paper Trading</Pill>
                            <Pill>Live Charts</Pill>
                        </div>
                    </div>

                    {/* Quick nav / TOC */}
                    <Card>
                        <nav className="p-4 md:p-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 text-xs md:text-sm">
                            {[
                                ["quickstart", "Quick Start"],
                                ["choose-trade", "Choosing a Trade"],
                                ["daily-picks", "Daily Picks"],
                                ["top-trades", "Top Trades Today"],
                                ["search", "Search"],
                                ["charts", "Live Chart + Paper"],
                                ["glossary", "Glossary"],
                                ["faq", "FAQ"],
                                ["disclaimer", "Disclaimer"],
                            ].map(([id, label]) => (
                                <a
                                    key={id}
                                    href={`#${id}`}
                                    className="rounded-xl border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-zinc-200 hover:text-white hover:border-zinc-700 hover:bg-zinc-900 transition"
                                >
                                    {label}
                                </a>
                            ))}
                        </nav>
                    </Card>
                </header>

                {/* Intro blurb */}
                <div className="mt-6">
                    <Card>
                        <div className="p-5 md:p-6">
                            <p className="text-zinc-300 text-sm md:text-base">
                                MyStockPicksAI scans the market and ranks opportunities using a
                                composite score ("Score") and a probability-style indicator
                                ("Confidence %"). Use this guide to understand each feature and
                                follow a consistent process for selecting your trades.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2">
                                <button onClick={go("/daily-picks")} className="rounded-xl px-3 py-2 bg-emerald-500/20 text-emerald-300 border border-emerald-600/40 hover:bg-emerald-500/30">
                                    Go to Daily Picks
                                </button>
                                <button onClick={go("/top-trades")} className="rounded-xl px-3 py-2 bg-sky-500/20 text-sky-300 border border-sky-600/40 hover:bg-sky-500/30">
                                    Go to Top Trades
                                </button>
                                <button onClick={go("/paper-trading")} className="rounded-xl px-3 py-2 bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-600/40 hover:bg-fuchsia-500/30">
                                    Open Paper Trading
                                </button>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Quick Start */}
                <Section id="quickstart" title="Quick Start (2–Minute On‑Ramp)">
                    <ol className="list-decimal pl-5 space-y-2">
                        <Li>
                            Open <b>Daily Picks</b> and tap a ticker to view the <b>Live Chart</b>.
                        </Li>
                        <Li>
                            Check the <b>Score</b> and <b>Confidence %</b>; confirm trend and key levels on the chart.
                        </Li>
                        <Li>
                            If trading options, review <b>Strike</b>, <b>Expiration</b>, and <b>Entry/Exit</b> on <b>Top Options Trades</b>.
                        </Li>
                        <Li>
                            Place a <b>Paper Trade</b> first to validate the idea.
                        </Li>
                        <Li>
                            Size risk: avoid risking more than a small % of your portfolio per trade.
                        </Li>
                    </ol>
                </Section>

                {/* Choosing a Trade */}
                <Section id="choose-trade" title="How to Choose Which Trade to Take">
                    <Card className="p-5 md:p-6 space-y-4">
                        <div>
                            <h3 className="text-lg font-semibold text-white">Decision Checklist</h3>
                            <ul className="list-disc pl-5 space-y-1">
                                <Li>
                                    <b>Alignment:</b> High Score + Confidence %, and the chart trend agrees (higher highs for longs; lower lows for shorts).
                                </Li>
                                <Li>
                                    <b>Clear plan:</b> Entry, target (Exit), and invalidation (stop) are obvious on the chart.
                                </Li>
                                <Li>
                                    <b>Liquidity:</b> For options, ensure decent volume/open interest; avoid illiquid contracts.
                                </Li>
                                <Li>
                                    <b>Time:</b> Pick expirations that give the trade time to work (avoid zero‑DTE unless that’s your strategy).
                                </Li>
                                <Li>
                                    <b>Risk:</b> Position size keeps max loss tolerable (e.g., ≤1–2% of account per idea).
                                </Li>
                            </ul>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-white">Simple Framework</h3>
                            <div className="grid md:grid-cols-3 gap-3">
                                <div className="rounded-xl p-4 border border-emerald-900/50 bg-emerald-900/10">
                                    <p className="text-emerald-300 font-medium">Conservative</p>
                                    <p className="text-zinc-300 text-sm mt-1">
                                        High Score (top tier), Confidence ≥ 65%, liquid options, clear trend. Paper trade first, then small size.
                                    </p>
                                </div>
                                <div className="rounded-xl p-4 border border-amber-900/50 bg-amber-900/10">
                                    <p className="text-amber-300 font-medium">Balanced</p>
                                    <p className="text-zinc-300 text-sm mt-1">
                                        Good Score, Confidence 55–65%, trend OK, time to expiration. Moderate size with strict stop.
                                    </p>
                                </div>
                                <div className="rounded-xl p-4 border border-rose-900/50 bg-rose-900/10">
                                    <p className="text-rose-300 font-medium">Aggressive</p>
                                    <p className="text-zinc-300 text-sm mt-1">
                                        Speculative setups (Confidence &lt; 55%) or fast scalps. Use tiny size and tight risk.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Card>
                </Section>

                {/* Features */}
                <Section id="daily-picks" title="Daily Picks">
                    <Card className="p-5 md:p-6">
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <p>
                                    Shows the platform’s <b>Top 3 stock picks</b> for the session. Each item includes
                                    a <b>Score</b> and <b>Confidence %</b>. Click any ticker to open its
                                    <b>Live Chart</b>.
                                </p>
                                <ul className="list-disc pl-5 space-y-1">
                                    <Li><b>Score</b> — composite strength ranking.</Li>
                                    <Li><b>Confidence %</b> — probability‑style indicator of edge.</Li>
                                    <Li><b>Action</b> — tap to view chart, levels, and plan.</Li>
                                </ul>
                            </div>
                            <div className="rounded-xl border border-zinc-800 p-4 bg-zinc-950/60">
                                <p className="text-zinc-400 text-sm mb-2">Tip</p>
                                <p className="text-zinc-300 text-sm">
                                    If picks are close in Score, prefer symbols with cleaner trends and higher liquidity.
                                </p>
                            </div>
                        </div>
                    </Card>
                </Section>

                <Section id="top-trades" title="Top Trades Today">
                    <Card className="p-5 md:p-6 space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <h3 className="text-white font-semibold">Top Options Trades</h3>
                                <ul className="list-disc pl-5 space-y-1">
                                    <Li><b>Strike Price</b> — target level for the option contract.</Li>
                                    <Li><b>Expiration</b> — last day the option is valid.</Li>
                                    <Li><b>Entry / Exit</b> — suggested buy and target levels.</Li>
                                </ul>
                            </div>
                            <div>
                                <h3 className="text-white font-semibold">Top NYSE Picks / Options</h3>
                                <p className="text-zinc-300 text-sm">
                                    Ranks opportunities across the entire NYSE. Sort or filter, then click through to charts.
                                </p>
                            </div>
                        </div>
                        <div className="rounded-xl border border-zinc-800 p-4 bg-zinc-950/60">
                            <p className="text-zinc-400 text-sm mb-2">Sizing Hint</p>
                            <p className="text-zinc-300 text-sm">
                                Consider smaller size on weekly options and increase time for swing ideas. Liquidity matters.
                            </p>
                        </div>
                    </Card>
                </Section>

                <Section id="search" title="Search">
                    <Card className="p-5 md:p-6">
                        <p>
                            Look up any ticker to get a model score and (when available) top two option ideas with
                            <b> Strike</b>, <b>Expiration</b>, <b>Entry</b>, and <b>Exit</b>.
                        </p>
                        <ul className="list-disc pl-5 space-y-1 mt-2">
                            <Li>Use the search to validate ideas beyond the daily lists.</Li>
                            <Li>Click results to open Live Chart for context.</Li>
                        </ul>
                    </Card>
                </Section>

                <Section id="charts" title="Live Chart + Paper Trading">
                    <Card className="p-5 md:p-6 space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <h3 className="text-white font-semibold">Live Chart</h3>
                                <ul className="list-disc pl-5 space-y-1">
                                    <Li>Switch between <b>candlestick</b> and <b>line</b> modes.</Li>
                                    <Li>Identify <b>support</b> / <b>resistance</b> and trend structure.</Li>
                                    <Li>Confirm that price respects your entry and stop levels.</Li>
                                </ul>
                            </div>
                            <div>
                                <h3 className="text-white font-semibold">Paper Trading</h3>
                                <ul className="list-disc pl-5 space-y-1">
                                    <Li>Test the idea before risking real capital.</Li>
                                    <Li>Track P&L and journal what worked or failed.</Li>
                                    <Li>Use outcomes to refine your selection rules.</Li>
                                </ul>
                            </div>
                        </div>
                    </Card>
                </Section>

                {/* Glossary */}
                <Section id="glossary" title="Glossary (Plain English)">
                    <div className="grid md:grid-cols-2 gap-4">
                        {[
                            ["Ticker", "The stock’s symbol (e.g., AAPL)."],
                            ["Score", "A composite ranking of setup quality."],
                            ["Confidence %", "Probability‑style indication of expected edge."],
                            ["Strike Price", "Set price level of an option contract."],
                            ["Expiration", "Last valid date for the option."],
                            ["Entry / Exit", "Suggested buy and target levels for a trade."],
                            ["Liquidity", "How easily you can get in/out (volume/open interest)."],
                            ["Risk per Trade", "% of account you’re willing to lose if wrong."],
                        ].map(([k, v]) => (
                            <Card key={k} className="p-4">
                                <p className="text-white font-medium">{k}</p>
                                <p className="text-zinc-300 text-sm mt-1">{v}</p>
                            </Card>
                        ))}
                    </div>
                </Section>

                {/* FAQ */}
                <Section id="faq" title="FAQ">
                    <div className="space-y-3">
                        {[
                            [
                                "How do I interpret Score vs. Confidence %?",
                                "Score ranks setups relative to each other; Confidence % approximates the model’s conviction. When both are high and the chart agrees, that’s stronger.",
                            ],
                            [
                                "Should I always pick the highest Score?",
                                "Not blindly. Prefer clean trends, liquid options, and clear risk levels even if the Score is slightly lower.",
                            ],
                            [
                                "What option expiration should I choose?",
                                "Give the idea time to work—avoid ultra‑short expirations unless that’s intentional. Swing ideas often benefit from more time.",
                            ],
                            [
                                "How big should my position be?",
                                "Keep risk small (e.g., 1–2% per trade). If new to options, start tiny and practice via paper trading.",
                            ],
                        ].map(([q, a]) => (
                            <details key={q} className="group rounded-2xl border border-zinc-800 bg-zinc-950/60 p-4">
                                <summary className="cursor-pointer list-none text-white font-medium flex items-center justify-between">
                                    {q}
                                    <span className="text-zinc-500 group-open:rotate-45 transition">＋</span>
                                </summary>
                                <p className="text-zinc-300 text-sm mt-2">{a}</p>
                            </details>
                        ))}
                    </div>
                </Section>

                {/* Disclaimer */}
                <Section id="disclaimer" title="Risk Disclaimer">
                    <Card className="p-5 md:p-6">
                        <p className="text-zinc-400 text-xs leading-relaxed">
                            MyStockPicksAI provides analytics and educational information only and does not offer
                            financial, investment, or trading advice. Markets carry risk; past performance does
                            not guarantee future results. Trade at your own risk and consider consulting a licensed
                            financial professional.
                        </p>
                    </Card>
                    <p className="mt-6 text-xs text-zinc-500">Last updated: {new Date().toLocaleDateString()}</p>
                </Section>
            </div>
        </div>
    );
}

